import React from 'react';
import { Award, Lightbulb, TrendingUp, History } from 'lucide-react';

export default function MetaSEOContent() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" itemScope itemType="https://schema.org/Article">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-center text-gray-900 mb-16" itemProp="headline">
            Meta Titles: The Ultimate Guide to SEO Optimization
          </h1>
          
          <div className="mb-16">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">
              Meta Title Generator: Boost Your Search Rankings
            </h2>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              Creating effective meta titles is crucial for improving your website's search engine visibility. Our Meta Title Generator helps you create optimized titles that enhance your SEO performance and click-through rates.
            </p>
            <div className="bg-emerald-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits of Using Our Meta Title Generator:</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  <span>SEO-optimized suggestions</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  <span>Character count monitoring</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  <span>Keyword integration</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  <span>Industry-specific formats</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <History className="w-6 h-6 text-emerald-500" />
                Evolution of Meta Titles
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Meta titles have evolved from simple page descriptions to powerful SEO tools that influence search rankings and user behavior.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-3 text-lg">
                <li>Historical SEO practices</li>
                <li>Search engine updates</li>
                <li>User behavior patterns</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
                Current Title Trends
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Modern meta titles focus on user intent while maintaining SEO best practices.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-3 text-lg">
                <li>Mobile optimization</li>
                <li>Voice search impact</li>
                <li>Featured snippet optimization</li>
              </ul>
            </div>
          </div>

          <div className="bg-emerald-50 p-12 rounded-2xl mb-20">
            <h3 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-3">
              <Award className="w-6 h-6 text-emerald-500" />
              Effective Title Characteristics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Relevance</h4>
                <p className="text-gray-600 text-lg">Accurately describes page content</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Optimization</h4>
                <p className="text-gray-600 text-lg">Includes target keywords naturally</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Length</h4>
                <p className="text-gray-600 text-lg">Optimal character count for display</p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h3 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-3">
              <Lightbulb className="w-6 h-6 text-emerald-500" />
              Expert Tips for Meta Titles
            </h3>
            <div className="space-y-6">
              <div className="border-l-4 border-emerald-500 pl-6 py-2">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Front-load Keywords</h4>
                <p className="text-gray-600 text-lg">Place important keywords near the beginning of your title.</p>
              </div>
              <div className="border-l-4 border-emerald-500 pl-6 py-2">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Monitor Length</h4>
                <p className="text-gray-600 text-lg">Keep titles between 50-60 characters to avoid truncation.</p>
              </div>
              <div className="border-l-4 border-emerald-500 pl-6 py-2">
                <h4 className="font-semibold text-gray-900 mb-3 text-xl">Be Specific</h4>
                <p className="text-gray-600 text-lg">Use clear, descriptive language that matches search intent.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-emerald-100 p-8 rounded-xl mb-20">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">
              Why Meta Titles Matter
            </h3>
            <div className="space-y-4 text-gray-700 text-lg">
              <p>Effective meta titles can:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Improve search rankings</li>
                <li>Increase suggested video appearances</li>
                <li>Reach target audience</li>
                <li>Boost video visibility</li>
                <li>Drive more views</li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 p-12 rounded-2xl">
            <h3 className="text-2xl font-semibold text-gray-900 mb-8">Popular Tag Categories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 text-xl">Content Type Tags</h4>
                <ul className="list-disc list-inside text-gray-600 space-y-3 text-lg">
                  <li>Tutorial tags</li>
                  <li>Review tags</li>
                  <li>Vlog tags</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 text-xl">Topic Tags</h4>
                <ul className="list-disc list-inside text-gray-600 space-y-3 text-lg">
                  <li>Gaming tags</li>
                  <li>Tech tags</li>
                  <li>Lifestyle tags</li>
                </ul>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}